import React, { Fragment, useState, useEffect, useCallback  } from 'react';
import axios from 'axios';
import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import { NotificationInline } from 'components/notifications';
import { LinkButton } from 'components/buttons';
import { VerticalLinkGroup, Breadcrumbs } from 'components/navigation';
import { UserGeneralSettings } from 'pages/users/edit/general-settings';
import { UserRole } from 'pages/users/edit/role';
import { UserChangelog } from 'pages/users/edit/changelog';
import { UserLocations } from 'pages/users/edit/locations'
import { AccountService, MfaService, UserService } from 'services';
import { ShowHide } from 'components/layout';
import { UserPreferences } from './preferences/UserPreferences';
import { UserAccountAccess } from './account-access';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserEmailHistory } from 'pages/users/edit/email-history';
import _ from 'underscore';
import { UserReportSchedules } from 'pages/users/edit/report-schedules';
import { UserMfaSettings } from 'pages/users/edit/mfa/UserMfaSettings';
import { UserAccountSwitching } from './account-switching';
import { UserAdditionalPermissions } from 'pages/users/edit/additional-permissions';

export function User(props) {
    const { id } = useParams();
    const [invalidUser, setInvalidUser] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [isDriver, setIsDriver] = useState(null);
    const [isPending, setIsPending] = useState(null);
    const [isChildParentAccount, setIsChildParentAccount] = useState(false);
    const [ssoAccount, setSsoAccount] = useState(false);
    const [mfaIsEnabled, setMfaIsEnabled] = useState(false);
    const [isCurrentInternalAccount, setIsCurrentInternalAccount] = useState(false);

    const internalAccountId = AccountService.internalAccountId();
    const requiredAction = "Users:Manage";
    const canAccessPage = UserHasAccess(requiredAction);

    const canAccessSchedules = UserHasAccess("ScheduleReports:Manage");

    const getRoutes = useCallback((hasDriverStatus, hasAccessAccount, userId, pendingUser, isMfaEnabled, isCurrentAccountInternal, simpleAccountSwitching) => {

        var pendingDriver = hasDriverStatus && pendingUser;

        var r = [
            { location: `/user/${userId}/general-settings`, title: "User Details" }
        ];

        if (!pendingDriver) {
            r.push({ location: `/user/${userId}/role`, title: "Role" });
        }

        if (hasDriverStatus != null && !hasDriverStatus) {
            r.push({ location: `/user/${userId}/locations`, title: "Locations" });
            r.push({ location: `/user/${userId}/preferences`, title: "Preferences" });
        }

        if (canAccessSchedules && !hasDriverStatus) {
            r.push({ location: `/user/${userId}/report-schedules`, title: "Report Schedules" });
        }

        if (isCurrentAccountInternal && simpleAccountSwitching) {
            r.push({ location: `/user/${userId}/account-switching`, title: "Switch Accounts" });
        } else if (hasDriverStatus != null && !hasDriverStatus && hasAccessAccount) {
            r.push({ location: `/user/${userId}/account-access`, title: "Account Access" });
        }

        if (hasDriverStatus != null && !hasDriverStatus) {
            r.push({ location: `/user/${userId}/additional-permissions`, title: "Additional Permissions" });
        }

        if (!pendingDriver) {
            r.push({ location: `/user/${userId}/changes`, title: "History" });
        }

        if (isMfaEnabled) {
            r.push({ location: `/user/${userId}/mfa`, title: "Multi-Factor Authentication" });
        }

        r.push({ location: `/user/${userId}/emails`, title: "Email History" });

        return r;
    }, [])

    const [user, setUser] = useState({
        role: {},
        firstName: "",
        lastName: "",
        emailAddress: "",
        simpleAccountSwitching: false
    });

    const [routes, setRoutes] = useState(getRoutes(isDriver, isChildParentAccount, id, isPending, mfaIsEnabled, isCurrentInternalAccount, user.simpleAccountSwitching));

    useEffect(() => {
        setRoutes(getRoutes(isDriver, isChildParentAccount, id, isPending, mfaIsEnabled, isCurrentInternalAccount, user.simpleAccountSwitching));
    }, [isDriver, isChildParentAccount, isPending, mfaIsEnabled, isCurrentInternalAccount, user.simpleAccountSwitching]);

    useEffect(() => {

        if (!canAccessPage) {
            return;
        }

        function setAccountAccessRoute() {
            axios.all([UserService.getLoggedInUser(),
            AccountService.getAllAccounts(), AccountService.getGeneralSettings(), MfaService.getUserMfaSettings(id)]).then(axios.spread(function (userDetails, accounts, accountGeneralSettings, mfaSettings) {
                var currentAccount = _.findWhere(accounts.data, { id: userDetails.accountId });
                var parentId = currentAccount.parentId != null ? currentAccount.parentId : userDetails.accountId;

                var relatedAccounts = _.filter(accounts.data, function (account) {
                    return account.parentId === parentId;
                });
                setIsChildParentAccount(relatedAccounts !== null && relatedAccounts.length > 0);
                setSsoAccount(accountGeneralSettings.ssoEnabled);
                setMfaIsEnabled(!!mfaSettings.activeMfaType && !accountGeneralSettings.ssoEnabled);
                setIsCurrentInternalAccount(userDetails.accountId === internalAccountId);
            }));
        }

        if (loadingData) {
            setAccountAccessRoute();
            UserService.getUserBasicInfo(id).then(r => {
                setUser(r);
                setLoadingData(false);
                setIsDriver(r.driverId != null);
                setIsPending(r.status === "Pending");
            }, (error) => {

                const noAccessCode = 403;

                if (error.status === noAccessCode) {
                    setInvalidUser(true);
                }
            });
        }
    }, [loadingData, id]);

    function onSave(modifiedUser) {
        setIsDriver(modifiedUser.role != null && modifiedUser.role.key != null && modifiedUser.role.key.toUpperCase() === "DRIVER");
        setUser(modifiedUser);
    }

    return (


        <UserAccess perform={requiredAction}
            yes={() => (

                <ShowHide
                    evaluator={invalidUser}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={props.breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="You do not have permission to view this user"
                                intent="danger">
                            </NotificationInline>
                            <div>
                                <LinkButton intent="primary" text="Back to user management" href="/user" id="return-user-listing" />
                            </div>
                        </div>
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={props.breadcrumbs} />
                                <ShowHide
                                    evaluator={user.firstName !== ""}
                                    show={(<h1>{user.firstName + " " + user.lastName}</h1>)}
                                    hide={(<h1>{user.emailAddress}</h1>)}
                                />
                                <ShowHide
                                    evaluator={user.disabledDate != null}
                                    show={(
                                        <div className="spacer-top spacer-bottom">
                                            <NotificationInline
                                                show
                                                text={(<span><strong>User Disabled: </strong>{user.firstName !== "" ? `${user.firstName} ${user.lastName}` : user.emailAddress} can no longer sign in to Vision.</span>)}
                                                intent="danger">
                                            </NotificationInline>
                                        </div>
                                    )}
                                />
                                <div className="intro-text">
                                    <p>Here you can configure your user settings for the different services provided by Vision.</p>
                                </div>
                            </div>

                            <div className="layout-cols">
                                <div className="col side-col">
                                    <div className="row">
                                        <VerticalLinkGroup links={routes}></VerticalLinkGroup>
                                    </div>
                                </div>
                                <div className="col content-col">
                                    <Routes>
                                        <Route path="account-access" element={<UserAccountAccess user={user} />} />
                                        <Route path="account-switching" element={<UserAccountSwitching user={user}></UserAccountSwitching>} />
                                        <Route path="additional-permissions" element={<UserAdditionalPermissions user={user} />} />
                                        <Route path="general-settings" element={<UserGeneralSettings user={user} loading={loadingData} ssoAccount={ssoAccount} onSave={onSave} />} />
                                        <Route path="" element={<Navigate to={`/user/${id}/general-settings`} replace />} />
                                        <Route path="role" element={<UserRole user={user} onSave={onSave} />} />
                                        <Route path="locations" element={<UserLocations userId={id} />} />
                                        <Route path="preferences" element={<UserPreferences userId={id} />} />
                                        <Route path="report-schedules" element={<UserReportSchedules userId={id} />} />
                                        <Route path="changes" element={<UserChangelog userId={id} />} />
                                        <Route path="mfa" element={<UserMfaSettings userId={id} editingOwnAccount={false} />} />
                                        <Route path="emails" element={<UserEmailHistory userId={id} />} />
                                    </Routes>
                                </div>
                            </div>
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );

}